import Alert from '@mui/material/Alert';
import { Size } from '@ps/web-core-enums';
import {
  StarsButton as Button,
  Link,
  Translation,
  useIsMounted,
  useScreenSize
} from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CMSImage } from '@swf/cms';
import WebpWithFallback from '@swf/components/WebpWithFallback/WebpWithFallback';
import useSignup from '@swf/core/hooks/useSignup';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { PriorityImage } from '@swf/poker';

import { useHomeGamesWidget } from '../../../model';
import { handleShowingBrandIconString } from '../../../utils/isPokerSiteLabel';
import bannerNonPoker from '../assets/banner-desktop-non-poker.png';
import bannerDesktopPNG from '../assets/banner-desktop.png';
import bannerDesktop from '../assets/banner-desktop.webp';
import bannerMobilePNG from '../assets/banner-mobile-tablet.png';
import bannerMobile from '../assets/banner-mobile-tablet.webp';
import logoPNG from '../assets/logo.png';
import logo from '../assets/logo.webp';
import type { StuckError } from '../hooks/useSessionLogger';
import { ERROR_TO_HIDE } from '../hooks/useSessionLogger';

import {
  TXTCLI_Login,
  TXTCLI_Reload,
  TXTMOB_Something_went_wrongX_Please_t_ELL,
  TXTWCL_If_you_still_experience_proble_ELL,
  TXTWCL_Join,
  TXTWCL_Must_deposit_to_play_real_mone_ELL,
  TXTWCL_Please_play_responsibly
} from './Banner.messages.json';
import styles from './Banner.scss';

export const Banner: React.FC<{ stuckError: StuckError }> = ({ stuckError }) => {
  const { widgetConfig } = useHomeGamesWidget();
  const {
    landing_section: { landing_title: landingTitle },
    promotion_club: promotionClub
  } = widgetConfig;
  const promoBannerDesktop = promotionClub.banner_image?.banner;
  const promoBannerMobile = promotionClub.banner_image?.banner_mobile;
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isPromo = !!promotionClub.club_id;
  const {
    modal: { enqueue }
  } = useDispatch<Dispatch>();
  const signup = useSignup();
  const { greaterThan } = useScreenSize();
  const isMounted = useIsMounted();
  const isMobileDevice = !greaterThan(Size.Medium);
  const shouldShowError = !!stuckError.code && !ERROR_TO_HIDE.includes(stuckError.code);
  const errorHiddenSign = `.${stuckError.hiddenSign}`;
  const bannerSrc = isMobileDevice ? bannerMobile : bannerDesktop;
  const bannerFallback = isMobileDevice ? bannerMobilePNG : bannerDesktopPNG;
  const isExternalPartnerSite = useSelector(select.cms.isExternalPartnerSite);

  const openLoginModal = useCallback(() => enqueue({ path: '/auth/login' }), [enqueue]);

  const imageUrl = handleShowingBrandIconString(isExternalPartnerSite, bannerNonPoker, bannerSrc);
  const fallbackUrl = handleShowingBrandIconString(
    isExternalPartnerSite,
    bannerNonPoker,
    bannerFallback
  );

  return (
    <section className={styles.banner}>
      <picture
        className={classNames({
          [styles.mobileBanner]: isMobileDevice,
          [styles.desktopBanner]: !isMobileDevice
        })}
      >
        {isPromo ? (
          <CMSImage
            asset={isMobileDevice ? promoBannerMobile : promoBannerDesktop}
            alt="Home Games promotional banner image"
          />
        ) : (
          <PriorityImage
            imageUrl={imageUrl}
            fallbackUrl={fallbackUrl}
            altText="Home Games banner image"
            fetchPriority="high"
          />
        )}
      </picture>
      {!isExternalPartnerSite && (
        <Link
          href="#"
          className={styles.logo}
          onClick={() => stuckError.hiddenSign && window.location.reload()}
        >
          <WebpWithFallback
            src={logo}
            srcFallback={logoPNG}
            imageSize={{ height: 711, width: 617 }}
          />
        </Link>
      )}
      <div className={styles.bannerInner}>
        <div className={styles.right}>
          <h1 className={styles.heading}>{landingTitle}</h1>
          {!isEmbedded && (
            <div className={styles.auth}>
              {!isExternalPartnerSite && (
                <Button
                  variant="primary"
                  className={styles.button}
                  size="extra-large"
                  inverse
                  loading={loading}
                  onClick={signup}
                  translation={TXTWCL_Join}
                />
              )}
              <Button
                className={styles.button}
                inverse
                loading={loading}
                onClick={openLoginModal}
                translation={TXTCLI_Login}
                size="extra-large"
                variant="casino-glass"
              />
            </div>
          )}
          <div className={styles.terms}>
            <Translation {...TXTWCL_Must_deposit_to_play_real_mone_ELL} /> <br />
            <StarsLink
              href="https://www.begambleaware.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              BeGambleAware.org
            </StarsLink>{' '}
            <Translation {...TXTWCL_Please_play_responsibly} />
            {isMounted() && errorHiddenSign}
          </div>
          {!!shouldShowError && (
            <Alert severity="error" variant="filled" className={styles.error}>
              <Translation {...TXTMOB_Something_went_wrongX_Please_t_ELL} />
              <Translation {...TXTWCL_If_you_still_experience_proble_ELL} />
              <br />
              <span>{`(${stuckError.code})`}</span>
              <br />
              <Button
                onClick={() => window.location.reload()}
                translation={TXTCLI_Reload}
                size="small"
              />
            </Alert>
          )}
        </div>
      </div>
    </section>
  );
};
